import { useState, useEffect, useCallback, useRef, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useUserRole } from "@/hooks/useUserRole";
import { useAdminActionQueueNotifications } from "@/hooks/useBrowserNotifications";
import { Navigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import OrderManagement from "@/components/admin/OrderManagement";
import OrderAnalytics from "@/components/admin/OrderAnalytics";
import SalesAttribution from "@/components/admin/SalesAttribution";
import SalespersonManagement from "@/components/admin/SalespersonManagement";
import SalesPerformanceReport from "@/components/admin/SalesPerformanceReport";
import MonthlyPayoutsDashboard from "@/components/admin/MonthlyPayoutsDashboard";
import PharmacyManagement from "@/components/admin/PharmacyManagement";
import ProviderCredentialing from "@/components/admin/ProviderCredentialing";
import StuckProviderAccounts from "@/components/admin/StuckProviderAccounts";
import EmailDashboard from "@/components/admin/EmailDashboard";
import MaintenanceRunsWidget from "@/components/admin/MaintenanceRunsWidget";
import CacLtvDashboard from "@/components/admin/CacLtvDashboard";
import PatientManagement from "@/components/admin/PatientManagement";
import AdminKPIOverview from "@/components/admin/AdminKPIOverview";
import AdminMessageOversight from "@/components/admin/AdminMessageOversight";
import ProviderPayouts from "@/components/admin/ProviderPayouts";
import QADashboard from "@/components/admin/QADashboard";
import PendingRoleRequests from "@/components/admin/PendingRoleRequests";
import AdminActionQueue from "@/components/admin/AdminActionQueue";
import AdminSidebar from "@/components/admin/AdminSidebar";
import AdminErrorLog from "@/components/admin/AdminErrorLog";
import CronJobsPanel from "@/components/admin/CronJobsPanel";
import OperationsToolkit from "@/components/admin/OperationsToolkit";
import ClearTestDataButton from "@/components/admin/ClearTestDataButton";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Label } from "@/components/ui/label";
import {
  Shield,
  Users,
  ScrollText,
  Activity,
  Search,
  RefreshCw,
  UserCheck,
  AlertTriangle,
  ShoppingCart,
  BarChart3,
  Briefcase,
  X,
  UserPlus,
  Mail,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Download,
  Building2,
  Stethoscope,
  CalendarIcon,
  DollarSign,
  Target,
  MessageSquare,
  ClipboardCheck,
  PanelLeft,
  Sparkles,
  KeyRound,
  Copy,
} from "lucide-react";
import { ChevronRight as BreadcrumbChevron } from "lucide-react";
import { format, startOfDay, endOfDay } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";

// ── Types ────────────────────────────────────────────────────────
interface UserWithRoles {
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  roles: string[];
}

interface UserProfile {
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  date_of_birth: string | null;
  sex: string | null;
  height: string | null;
  weight: number | null;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  created_at: string;
  roles: string[];
}

interface AuditLogEntry {
  id: string;
  user_id: string;
  action: string;
  resource_type: string;
  resource_id: string | null;
  created_at: string;
  ip_address: string | null;
  metadata: any;
  actor_role: string | null;
}

const ROLE_BADGE: Record<string, { label: string; className: string }> = {
  super_admin: {
    label: "Super Admin",
    className: "border-amber-500/50 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  },
  admin: {
    label: "Admin",
    className: "border-primary/40 bg-primary/5 text-primary",
  },
  provider: { label: "Provider", className: "border-border bg-muted text-muted-foreground" },
  pharmacy: { label: "Pharmacy", className: "border-border bg-muted text-muted-foreground" },
  salesperson: { label: "Sales", className: "border-border bg-muted text-muted-foreground" },
  user: { label: "User", className: "border-border bg-muted text-muted-foreground" },
};

const TAB_LABELS: Record<string, string> = {
  orders: "Orders",
  sales: "Sales",
  users: "Users & Roles",
  patients: "Patients",
  audit: "Audit Logs",
  providers: "Providers",
  pharmacies: "Pharmacies",
  emails: "Emails",
  messages: "Messages",
  payouts: "Payouts",
  "cac-ltv": "CAC / LTV",
  security: "Security",
  qa: "QA",
  invites: "Invites",
  errors: "User Errors",
  cron: "Cron Jobs",
  operations: "Operations",
};

// ── Main Component ───────────────────────────────────────────────
const AdminDashboard = () => {
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, isSuperAdmin, loading: roleLoading } = useUserRole();
  const [activeTab, setActiveTab] = useState("overview");
  const [ordersPatientFilter, setOrdersPatientFilter] = useState<string | null>(null);
  const [patientsHighlight, setPatientsHighlight] = useState<string | null>(null);

  // Enable browser push notifications for high-priority action queue items
  const { permission: adminPushPermission, requestPermission: requestAdminPush, isSupported: pushSupported } = useAdminActionQueueNotifications();

  const handleNavigateToTab = (tab: string) => {
    if (tab !== "orders") setOrdersPatientFilter(null);
    if (tab !== "patients") setPatientsHighlight(null);
    setActiveTab(tab);
  };

  const navigateToOrdersForPatient = (patientId: string) => {
    setOrdersPatientFilter(patientId);
    setActiveTab("orders");
  };

  const navigateToPatient = (patientId: string) => {
    setPatientsHighlight(patientId);
    setActiveTab("patients");
  };

  if (authLoading || roleLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;
  if (!isAdmin) return <Navigate to="/dashboard" replace />;

  const renderContent = () => {
    const label = TAB_LABELS[activeTab] || (activeTab === "overview" ? "Overview" : activeTab);
    let inner: ReactNode = null;
    switch (activeTab) {
      case "overview":
        inner = (
          <>
            {pushSupported && adminPushPermission === "default" && (
              <div className="mb-4 flex items-center gap-3 rounded-lg border border-primary/20 bg-primary/5 p-3">
                <span className="text-sm text-muted-foreground">
                  🔔 Get browser notifications when high-priority items need your attention.
                </span>
                <Button size="sm" variant="outline" className="shrink-0" onClick={requestAdminPush}>
                  Enable Notifications
                </Button>
              </div>
            )}
            <ErrorBoundary label="Action queue"><AdminActionQueue onNavigateToTab={handleNavigateToTab} /></ErrorBoundary>
            <ErrorBoundary label="Stuck providers"><StuckProviderAccounts onNavigateToTab={handleNavigateToTab} /></ErrorBoundary>
            <ErrorBoundary label="Maintenance runs"><MaintenanceRunsWidget /></ErrorBoundary>
            <ErrorBoundary label="KPI overview"><AdminKPIOverview onNavigateToTab={handleNavigateToTab} /></ErrorBoundary>
          </>
        );
        break;
      case "orders":
        inner = <OrderManagement patientFilter={ordersPatientFilter} onClearPatientFilter={() => setOrdersPatientFilter(null)} onNavigateToPatient={navigateToPatient} />;
        break;
      case "analytics":
        inner = <OrderAnalytics />;
        break;
      case "sales":
        inner = <SalesSubTabs />;
        break;
      case "patients":
        inner = <PatientManagement onNavigateToOrders={navigateToOrdersForPatient} highlightPatientId={patientsHighlight} />;
        break;
      case "users":
        inner = <UsersTab />;
        break;
      case "audit":
        inner = <AuditTab />;
        break;
      case "pharmacies":
        inner = <PharmacyManagement />;
        break;
      case "providers":
        inner = <ProviderCredentialing />;
        break;
      case "emails":
        inner = <EmailDashboard />;
        break;
      case "messages":
        inner = <AdminMessageOversight />;
        break;
      case "payouts":
        inner = <PayoutsSubTabs />;
        break;
      case "cac-ltv":
        inner = <CacLtvDashboard />;
        break;
      case "security":
        inner = <SecurityTab />;
        break;
      case "qa":
        inner = <QADashboard />;
        break;
      case "invites":
        inner = <PendingRoleRequests />;
        break;
      case "errors":
        inner = <AdminErrorLog />;
        break;
      case "cron":
        inner = <CronJobsPanel />;
        break;
      case "operations":
        inner = <OperationsToolkit />;
        break;
      default:
        return null;
    }
    if (activeTab === "overview") return inner;
    return <ErrorBoundary label={`${label} tab`}>{inner}</ErrorBoundary>;
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="pt-16">
        <SidebarProvider>
          <div className="min-h-[calc(100vh-4rem)] flex w-full">
            <AdminSidebar activeTab={activeTab} onNavigateToTab={handleNavigateToTab} />
            <main className="flex-1 overflow-auto">
              <div className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <SidebarTrigger className="shrink-0">
                    <PanelLeft size={18} />
                  </SidebarTrigger>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Shield size={18} className="text-primary" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h1 className="font-display text-2xl text-foreground leading-tight">Admin Dashboard</h1>
                        {isSuperAdmin ? (
                          <Badge
                            className="text-[10px] uppercase tracking-wide font-semibold border-amber-500/50 bg-amber-500/10 text-amber-700 dark:text-amber-300 hover:bg-amber-500/15"
                            variant="outline"
                            title="Super Admin — full access to financial, identity, and destructive controls"
                          >
                            Super Admin
                          </Badge>
                        ) : (
                          <Badge
                            variant="outline"
                            className="text-[10px] uppercase tracking-wide font-semibold border-primary/40 bg-primary/5 text-primary"
                            title="Admin — day-to-day operations access"
                          >
                            Admin
                          </Badge>
                        )}
                      </div>
                      {activeTab !== "overview" && (
                        <nav className="flex items-center gap-1.5 text-xs mt-0.5">
                          <button
                            onClick={() => handleNavigateToTab("overview")}
                            className="text-muted-foreground hover:text-primary transition-colors font-medium"
                          >
                            Overview
                          </button>
                          <BreadcrumbChevron size={12} className="text-muted-foreground/50" />
                          <span className="text-foreground font-medium">
                            {TAB_LABELS[activeTab] || activeTab}
                          </span>
                        </nav>
                      )}
                    </div>
                  </div>
                  <div className="ml-auto">
                    <ClearTestDataButton />
                  </div>
                </div>
                {renderContent()}
              </div>
            </main>
          </div>
        </SidebarProvider>
      </div>
    </div>
  );
};
// ── Sales Sub-Tabs ───────────────────────────────────────────────
const SalesSubTabs = () => {
  const [subTab, setSubTab] = useState("attribution");
  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        {[
          { value: "attribution", label: "Attribution" },
          { value: "manage", label: "Manage" },
          { value: "performance", label: "Performance" },
        ].map((t) => (
          <Button
            key={t.value}
            variant={subTab === t.value ? "default" : "outline"}
            size="sm"
            onClick={() => setSubTab(t.value)}
          >
            {t.label}
          </Button>
        ))}
      </div>
      {subTab === "attribution" && <SalesAttribution />}
      {subTab === "manage" && <SalespersonManagement />}
      {subTab === "performance" && <SalesPerformanceReport />}
    </div>
  );
};

// ── Payouts Sub-Tabs ────────────────────────────────────────────
const PayoutsSubTabs = () => {
  const [subTab, setSubTab] = useState("unified");
  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        {[
          { value: "unified", label: "Monthly Overview" },
          { value: "sales", label: "Sales Commissions" },
          { value: "providers", label: "Provider Consultation Fees" },
        ].map((t) => (
          <Button
            key={t.value}
            variant={subTab === t.value ? "default" : "outline"}
            size="sm"
            onClick={() => setSubTab(t.value)}
          >
            {t.label}
          </Button>
        ))}
      </div>
      {subTab === "unified" && <MonthlyPayoutsDashboard />}
      {subTab === "sales" && <SalespersonManagement />}
      {subTab === "providers" && <ProviderPayouts />}
    </div>
  );
};

const USERS_PER_PAGE = 10;

const UsersTab = () => {
  const [users, setUsers] = useState<UserWithRoles[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pendingRemoval, setPendingRemoval] = useState<{ userId: string; role: string; userName: string } | null>(null);
  const [pendingDelete, setPendingDelete] = useState<{ userId: string; userName: string } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteFirstName, setInviteFirstName] = useState("");
  const [inviteLastName, setInviteLastName] = useState("");
  const [inviteRole, setInviteRole] = useState<string>("");
  const [inviting, setInviting] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState<Partial<UserProfile>>({});
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const openUserProfile = async (u: UserWithRoles) => {
    setProfileLoading(true);
    setSelectedUser({
      user_id: u.user_id,
      first_name: u.first_name,
      last_name: u.last_name,
      email: u.email,
      phone: null,
      date_of_birth: null,
      sex: null,
      height: null,
      weight: null,
      address_line1: null,
      address_line2: null,
      city: null,
      state: null,
      zip_code: null,
      created_at: "",
      roles: u.roles,
    });
    const { data } = await supabase
      .from("profiles")
      .select("phone, date_of_birth, sex, height, weight, address_line1, address_line2, city, state, zip_code, created_at")
      .eq("user_id", u.user_id)
      .single();
    setSelectedUser((prev) =>
      prev
        ? {
            ...prev,
            phone: data?.phone || null,
            date_of_birth: data?.date_of_birth || null,
            sex: data?.sex || null,
            height: data?.height || null,
            weight: data?.weight != null ? Number(data.weight) : null,
            address_line1: data?.address_line1 || null,
            address_line2: data?.address_line2 || null,
            city: data?.city || null,
            state: data?.state || null,
            zip_code: data?.zip_code || null,
            created_at: data?.created_at || "",
          }
        : null
    );
    setProfileLoading(false);
  };

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    const [{ data: profiles }, { data: roles }, emailResult] = await Promise.all([
      supabase.from("profiles").select("user_id, first_name, last_name").order("created_at", { ascending: false }),
      supabase.from("user_roles").select("user_id, role"),
      supabase.functions.invoke("list-user-emails"),
    ]);

    const emailMap: Record<string, string> = emailResult.data?.emails || {};

    if (profiles) {
      const roleMap = new Map<string, string[]>();
      roles?.forEach((r: any) => {
        const existing = roleMap.get(r.user_id) || [];
        existing.push(r.role);
        roleMap.set(r.user_id, existing);
      });

      setUsers(
        profiles.map((p: any) => ({
          user_id: p.user_id,
          first_name: p.first_name,
          last_name: p.last_name,
          email: emailMap[p.user_id] || null,
          roles: roleMap.get(p.user_id) || [],
        }))
      );
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleRoleChange = async (userId: string, newRole: string) => {
    if (newRole === "provider" || newRole === "salesperson" || newRole === "admin") {
      const { error } = await supabase
        .from("user_roles")
        .insert({ user_id: userId, role: newRole as any });
      if (error) {
        toast({ title: "Failed to assign role", description: error.message, variant: "destructive" });
      } else {
        toast({ title: "Role assigned", description: `${newRole} role added successfully.` });
        fetchUsers();
      }
    }
  };

  const confirmRoleRemove = (userId: string, role: string) => {
    const user = users.find((u) => u.user_id === userId);
    const userName = user?.first_name || user?.last_name
      ? `${user.first_name || ""} ${user.last_name || ""}`.trim()
      : userId.slice(0, 8) + "…";
    setPendingRemoval({ userId, role, userName });
  };

  const handleRoleRemove = async () => {
    if (!pendingRemoval) return;
    const { userId, role } = pendingRemoval;
    setPendingRemoval(null);
    const { error } = await supabase
      .from("user_roles")
      .delete()
      .eq("user_id", userId)
      .eq("role", role as any);
    if (error) {
      toast({ title: "Failed to remove role", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Role removed", description: `${role} role removed successfully.` });
      fetchUsers();
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      const { data, error } = await supabase.functions.invoke("invite-user", {
        body: {
          email: inviteEmail.trim(),
          ...(inviteFirstName.trim() ? { firstName: inviteFirstName.trim() } : {}),
          ...(inviteLastName.trim() ? { lastName: inviteLastName.trim() } : {}),
          ...(inviteRole && inviteRole !== "none" ? { role: inviteRole } : {}),
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast({ title: "Invitation sent", description: `An invite email has been sent to ${inviteEmail}.` });
      setInviteEmail("");
      setInviteFirstName("");
      setInviteLastName("");
      setInviteRole("");
      setInviteOpen(false);
      fetchUsers();
    } catch (err: any) {
      toast({ title: "Invite failed", description: err.message || "Something went wrong.", variant: "destructive" });
    } finally {
      setInviting(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      const { data, error } = await supabase.functions.invoke("delete-user", {
        body: { user_id: pendingDelete.userId },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast({ title: "User deleted", description: `${pendingDelete.userName} has been permanently deleted.` });
      setPendingDelete(null);
      fetchUsers();
    } catch (err: any) {
      toast({ title: "Failed to delete user", description: err.message, variant: "destructive" });
    } finally {
      setDeleting(false);
    }
  };

  const confirmDeleteUser = (userId: string) => {
    const user = users.find((u) => u.user_id === userId);
    const userName = user?.first_name || user?.last_name
      ? `${user.first_name || ""} ${user.last_name || ""}`.trim()
      : userId.slice(0, 8) + "…";
    setPendingDelete({ userId, userName });
  };

  const filteredUsers = users.filter((u) => {
    const name = `${u.first_name || ""} ${u.last_name || ""}`.toLowerCase();
    const email = (u.email || "").toLowerCase();
    const term = search.toLowerCase();
    return name.includes(term) || u.user_id.includes(search) || email.includes(term);
  });

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / USERS_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedUsers = filteredUsers.slice((safePage - 1) * USERS_PER_PAGE, safePage * USERS_PER_PAGE);

  // Reset to page 1 when search changes
  useEffect(() => { setCurrentPage(1); }, [search]);

  return (
    <>
    <Card className="border-border">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <CardTitle className="font-display text-xl">All Users</CardTitle>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search users..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 w-64"
            />
          </div>
          <Button variant="outline" size="icon" onClick={fetchUsers}>
            <RefreshCw size={14} />
          </Button>
          <Button variant="outline" size="sm" className="gap-1.5" onClick={() => {
            const csvRows = [["Name", "Email", "User ID", "Roles"]];
            filteredUsers.forEach((u) => {
              const name = `${u.first_name || ""} ${u.last_name || ""}`.trim() || "—";
              csvRows.push([name, u.email || "", u.user_id, u.roles.length ? u.roles.join("; ") : "user"]);
            });
            const csv = csvRows.map(r => r.map(c => `"${c.replace(/"/g, '""')}"`).join(",")).join("\n");
            const blob = new Blob([csv], { type: "text/csv" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `users-export-${new Date().toISOString().slice(0, 10)}.csv`;
            a.click();
            URL.revokeObjectURL(url);
          }}>
            <Download size={14} /> Export CSV
          </Button>
          <Button size="sm" className="gap-1.5" onClick={() => setInviteOpen(true)}>
            <UserPlus size={14} /> Invite User
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Roles</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedUsers.map((u) => (
                <TableRow key={u.user_id} className="cursor-pointer" onClick={() => openUserProfile(u)}>
                  <TableCell className="font-medium">
                    {u.first_name || u.last_name
                      ? `${u.first_name || ""} ${u.last_name || ""}`.trim()
                      : "—"}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground truncate max-w-[200px]" title={u.email || ""}>
                    {u.email || "—"}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1.5">
                      {u.roles.length === 0 && (
                        <Badge variant="secondary" className="text-xs">user</Badge>
                      )}
                      {u.roles.map((r) => (
                        <Badge
                          key={r}
                          variant={r === "admin" ? "default" : r === "provider" ? "outline" : r === "salesperson" ? "secondary" : "secondary"}
                          className={`text-xs flex items-center gap-1 ${r === "salesperson" ? "border-champagne text-champagne" : ""}`}
                        >
                          {r}
                          <button
                            onClick={(e) => { e.stopPropagation(); confirmRoleRemove(u.user_id, r); }}
                            className="ml-0.5 hover:text-destructive transition-colors"
                            title={`Remove ${r} role`}
                          >
                            <X size={12} />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-2">
                      <Select onValueChange={(val) => handleRoleChange(u.user_id, val)}>
                        <SelectTrigger className="w-36 h-8 text-xs">
                          <SelectValue placeholder="Assign role..." />
                        </SelectTrigger>
                        <SelectContent>
                          {!u.roles.includes("admin") && <SelectItem value="admin">Make Admin</SelectItem>}
                          {!u.roles.includes("provider") && <SelectItem value="provider">Make Provider</SelectItem>}
                          {!u.roles.includes("salesperson") && <SelectItem value="salesperson">Make Salesperson</SelectItem>}
                        </SelectContent>
                      </Select>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        onClick={() => confirmDeleteUser(u.user_id)}
                        title="Delete user"
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {filteredUsers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                    No users found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
        {totalPages > 1 && (
          <div className="flex items-center justify-between pt-4 px-2">
            <p className="text-sm text-muted-foreground">
              Showing {(safePage - 1) * USERS_PER_PAGE + 1}–{Math.min(safePage * USERS_PER_PAGE, filteredUsers.length)} of {filteredUsers.length} users
            </p>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="icon" className="h-8 w-8" disabled={safePage <= 1} onClick={() => setCurrentPage(safePage - 1)}>
                <ChevronLeft size={14} />
              </Button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - safePage) <= 1)
                .reduce<(number | 'ellipsis')[]>((acc, p, i, arr) => {
                  if (i > 0 && p - (arr[i - 1] as number) > 1) acc.push('ellipsis');
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, i) =>
                  p === 'ellipsis' ? (
                    <span key={`e${i}`} className="px-1 text-muted-foreground text-sm">…</span>
                  ) : (
                    <Button key={p} variant={p === safePage ? "default" : "outline"} size="icon" className="h-8 w-8 text-xs" onClick={() => setCurrentPage(p)}>
                      {p}
                    </Button>
                  )
                )}
              <Button variant="outline" size="icon" className="h-8 w-8" disabled={safePage >= totalPages} onClick={() => setCurrentPage(safePage + 1)}>
                <ChevronRight size={14} />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>

      <AlertDialog open={!!pendingRemoval} onOpenChange={(open) => !open && setPendingRemoval(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove role?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove the <strong>{pendingRemoval?.role}</strong> role from <strong>{pendingRemoval?.userName}</strong>? This will immediately revoke their access to {pendingRemoval?.role} features.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRoleRemove} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Remove Role
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!pendingDelete} onOpenChange={(open) => !open && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete user permanently?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <strong>{pendingDelete?.userName}</strong> and all their associated data (profile, roles, appointments, assessments). This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteUser}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? "Deleting…" : "Delete User"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={inviteOpen} onOpenChange={(open) => { if (!open) { setInviteOpen(false); setInviteEmail(""); setInviteFirstName(""); setInviteLastName(""); setInviteRole(""); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail size={18} /> Invite User
            </DialogTitle>
            <DialogDescription>
              Send an invitation email. The user will receive a link to set up their account.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="invite-first-name">First name</Label>
                <Input
                  id="invite-first-name"
                  placeholder="Jane"
                  value={inviteFirstName}
                  onChange={(e) => setInviteFirstName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="invite-last-name">Last name</Label>
                <Input
                  id="invite-last-name"
                  placeholder="Doe"
                  value={inviteLastName}
                  onChange={(e) => setInviteLastName(e.target.value)}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="invite-email">Email address</Label>
              <Input
                id="invite-email"
                type="email"
                placeholder="user@example.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Assign role (optional)</Label>
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger>
                  <SelectValue placeholder="No role — basic user" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No role — basic user</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="provider">Provider</SelectItem>
                  <SelectItem value="salesperson">Salesperson</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteOpen(false)}>Cancel</Button>
            <Button onClick={handleInvite} disabled={inviting || !inviteEmail.trim()}>
              {inviting ? "Sending…" : "Send Invite"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!selectedUser} onOpenChange={(open) => { if (!open) { setSelectedUser(null); setEditing(false); } }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCheck size={18} /> {editing ? "Edit User" : "User Profile"}
            </DialogTitle>
            <DialogDescription>
              {editing ? "Update this user's profile information." : "Full profile details for this user."}
            </DialogDescription>
          </DialogHeader>
          {selectedUser && !editing && (
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">First Name</p>
                  <p className="text-sm font-medium">{selectedUser.first_name || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Last Name</p>
                  <p className="text-sm font-medium">{selectedUser.last_name || "—"}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-muted-foreground">Email</p>
                  <p className="text-sm font-medium">{selectedUser.email || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Phone</p>
                  <p className="text-sm font-medium">{profileLoading ? "Loading…" : selectedUser.phone || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Date of Birth</p>
                  <p className="text-sm font-medium">
                    {profileLoading ? "Loading…" : selectedUser.date_of_birth ? format(new Date(selectedUser.date_of_birth), "MMM d, yyyy") : "—"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Sex</p>
                  <p className="text-sm font-medium capitalize">{profileLoading ? "Loading…" : selectedUser.sex || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Height</p>
                  <p className="text-sm font-medium">{profileLoading ? "Loading…" : selectedUser.height || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Weight</p>
                  <p className="text-sm font-medium">{profileLoading ? "Loading…" : selectedUser.weight != null ? `${selectedUser.weight} lbs` : "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Member Since</p>
                  <p className="text-sm font-medium">
                    {profileLoading ? "Loading…" : selectedUser.created_at ? format(new Date(selectedUser.created_at), "MMM d, yyyy") : "—"}
                  </p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-muted-foreground">Address</p>
                  <p className="text-sm font-medium">
                    {profileLoading ? "Loading…" : (
                      [selectedUser.address_line1, selectedUser.address_line2, [selectedUser.city, selectedUser.state, selectedUser.zip_code].filter(Boolean).join(", ")].filter(Boolean).join(", ") || "—"
                    )}
                  </p>
                </div>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1.5">Roles</p>
                <div className="flex gap-1.5">
                  {selectedUser.roles.length === 0 && <Badge variant="secondary" className="text-xs">user</Badge>}
                  {selectedUser.roles.map((r) => (
                    <Badge
                      key={r}
                      variant={r === "admin" ? "default" : r === "provider" ? "outline" : "secondary"}
                      className={`text-xs ${r === "salesperson" ? "border-champagne text-champagne" : ""}`}
                    >
                      {r}
                    </Badge>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">User ID</p>
                <p className="text-xs font-mono text-muted-foreground">{selectedUser.user_id}</p>
              </div>
            </div>
          )}
          {selectedUser && editing && (
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="edit-first-name" className="text-xs">First Name</Label>
                  <Input id="edit-first-name" value={editForm.first_name || ""} onChange={(e) => setEditForm((f) => ({ ...f, first_name: e.target.value }))} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="edit-last-name" className="text-xs">Last Name</Label>
                  <Input id="edit-last-name" value={editForm.last_name || ""} onChange={(e) => setEditForm((f) => ({ ...f, last_name: e.target.value }))} />
                </div>
                <div className="col-span-2 space-y-1.5">
                  <Label htmlFor="edit-email" className="text-xs">Email</Label>
                  <Input id="edit-email" type="email" value={editForm.email || ""} onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="edit-phone" className="text-xs">Phone</Label>
                  <Input id="edit-phone" value={editForm.phone || ""} onChange={(e) => setEditForm((f) => ({ ...f, phone: e.target.value }))} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="edit-dob" className="text-xs">Date of Birth</Label>
                  <Input id="edit-dob" type="date" value={editForm.date_of_birth || ""} onChange={(e) => setEditForm((f) => ({ ...f, date_of_birth: e.target.value }))} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="edit-sex" className="text-xs">Sex</Label>
                  <Select value={editForm.sex || ""} onValueChange={(v) => setEditForm((f) => ({ ...f, sex: v }))}>
                    <SelectTrigger id="edit-sex"><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="edit-height" className="text-xs">Height</Label>
                  <Input id="edit-height" placeholder="e.g. 5'10&quot;" value={editForm.height || ""} onChange={(e) => setEditForm((f) => ({ ...f, height: e.target.value }))} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="edit-weight" className="text-xs">Weight (lbs)</Label>
                  <Input id="edit-weight" type="number" value={editForm.weight != null ? String(editForm.weight) : ""} onChange={(e) => setEditForm((f) => ({ ...f, weight: e.target.value ? Number(e.target.value) : null }))} />
                </div>
                <div className="col-span-2 space-y-1.5">
                  <Label htmlFor="edit-address1" className="text-xs">Street Address</Label>
                  <Input id="edit-address1" value={editForm.address_line1 || ""} onChange={(e) => setEditForm((f) => ({ ...f, address_line1: e.target.value }))} />
                </div>
                <div className="col-span-2 space-y-1.5">
                  <Label htmlFor="edit-address2" className="text-xs">Address Line 2</Label>
                  <Input id="edit-address2" placeholder="Apt, suite, etc." value={editForm.address_line2 || ""} onChange={(e) => setEditForm((f) => ({ ...f, address_line2: e.target.value }))} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="edit-city" className="text-xs">City</Label>
                  <Input id="edit-city" value={editForm.city || ""} onChange={(e) => setEditForm((f) => ({ ...f, city: e.target.value }))} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="edit-state" className="text-xs">State</Label>
                  <Input id="edit-state" value={editForm.state || ""} onChange={(e) => setEditForm((f) => ({ ...f, state: e.target.value }))} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="edit-zip" className="text-xs">ZIP Code</Label>
                  <Input id="edit-zip" value={editForm.zip_code || ""} onChange={(e) => setEditForm((f) => ({ ...f, zip_code: e.target.value }))} />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            {selectedUser && !editing && !profileLoading && (
              <Button onClick={() => { setEditing(true); setEditForm({ first_name: selectedUser.first_name, last_name: selectedUser.last_name, email: selectedUser.email, phone: selectedUser.phone, date_of_birth: selectedUser.date_of_birth, sex: selectedUser.sex, height: selectedUser.height, weight: selectedUser.weight, address_line1: selectedUser.address_line1, address_line2: selectedUser.address_line2, city: selectedUser.city, state: selectedUser.state, zip_code: selectedUser.zip_code }); }}>
                Edit Profile
              </Button>
            )}
            {editing && (
              <>
                <Button variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
                <Button disabled={saving} onClick={async () => {
                  if (!selectedUser) return;
                  setSaving(true);

                  // Update email via edge function if changed
                  const emailChanged = (editForm.email || "").trim().toLowerCase() !== (selectedUser.email || "").trim().toLowerCase();
                  if (emailChanged && editForm.email?.trim()) {
                    const { data, error: emailError } = await supabase.functions.invoke("admin-update-email", {
                      body: { user_id: selectedUser.user_id, email: editForm.email.trim() },
                    });
                    if (emailError || data?.error) {
                      setSaving(false);
                      toast({ title: "Email update failed", description: data?.error || emailError?.message || "Could not update email.", variant: "destructive" });
                      return;
                    }
                  }

                  const { error } = await supabase.from("profiles").update({
                    first_name: editForm.first_name || null,
                    last_name: editForm.last_name || null,
                    phone: editForm.phone || null,
                    date_of_birth: editForm.date_of_birth || null,
                    sex: editForm.sex || null,
                    height: editForm.height || null,
                    weight: editForm.weight != null ? editForm.weight : null,
                    address_line1: editForm.address_line1 || null,
                    address_line2: editForm.address_line2 || null,
                    city: editForm.city || null,
                    state: editForm.state || null,
                    zip_code: editForm.zip_code || null,
                  }).eq("user_id", selectedUser.user_id);
                  setSaving(false);
                  if (error) {
                    toast({ title: "Save failed", description: error.message, variant: "destructive" });
                  } else {
                    toast({ title: "Profile updated", description: "User information saved successfully." });
                    setSelectedUser((prev) => prev ? { ...prev, ...editForm } : null);
                    setEditing(false);
                    fetchUsers();
                  }
                }}>
                  {saving ? "Saving…" : "Save Changes"}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// ── Audit Logs Tab ───────────────────────────────────────────────
const PAGE_SIZE = 50;

const AuditTab = () => {
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [dateFrom, setDateFrom] = useState<Date | undefined>(undefined);
  const [dateTo, setDateTo] = useState<Date | undefined>(undefined);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    const from = page * PAGE_SIZE;
    const to = from + PAGE_SIZE - 1;

    let countQuery = supabase
      .from("audit_logs")
      .select("*", { count: "exact", head: true });
    if (filter !== "all") countQuery = countQuery.eq("resource_type", filter);
    if (dateFrom) countQuery = countQuery.gte("created_at", startOfDay(dateFrom).toISOString());
    if (dateTo) countQuery = countQuery.lte("created_at", endOfDay(dateTo).toISOString());
    const { count } = await countQuery;
    setTotalCount(count || 0);

    let query = supabase
      .from("audit_logs")
      .select("*")
      .order("created_at", { ascending: false })
      .range(from, to);

    if (filter !== "all") {
      query = query.eq("resource_type", filter);
    }
    if (dateFrom) query = query.gte("created_at", startOfDay(dateFrom).toISOString());
    if (dateTo) query = query.lte("created_at", endOfDay(dateTo).toISOString());

    const { data } = await query;
    setLogs((data as any) || []);
    setLoading(false);
  }, [filter, page, dateFrom, dateTo]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Reset to first page when filter changes
  useEffect(() => {
    setPage(0);
  }, [filter, dateFrom, dateTo]);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const actionColor = (action: string): "secondary" | "destructive" | "outline" | "default" => {
    if (action === "approve_provider") return "default";
    if (action === "reject_provider" || action === "revoke_provider") return "destructive";
    if (action === "reinstate_provider") return "secondary";
    if (action.startsWith("view")) return "secondary";
    if (action.includes("cancel") || action.includes("delete")) return "destructive";
    return "outline";
  };

  const exportCsv = async () => {
    let query = supabase
      .from("audit_logs")
      .select("*")
      .order("created_at", { ascending: false });
    if (filter !== "all") query = query.eq("resource_type", filter);
    if (dateFrom) query = query.gte("created_at", startOfDay(dateFrom).toISOString());
    if (dateTo) query = query.lte("created_at", endOfDay(dateTo).toISOString());

    const { data } = await query;
    if (!data || data.length === 0) return;

    const headers = ["Date", "Actor Role", "Action", "Resource Type", "Resource ID", "User ID", "IP Address", "Provider Name", "Previous Status", "Reason"];
    const rows = data.map((l: any) => [
      new Date(l.created_at).toISOString(),
      l.actor_role || "",
      l.action,
      l.resource_type,
      l.resource_id || "",
      l.user_id,
      l.ip_address || "",
      l.metadata?.provider_name || "",
      l.metadata?.previous_status || "",
      l.metadata?.reason || "",
    ]);

    const csvContent = [headers, ...rows]
      .map((r) => r.map((c: string) => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `audit-logs-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Card className="border-border">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <div>
          <CardTitle className="font-display text-xl">Audit Logs</CardTitle>
          {!loading && (
            <p className="text-xs text-muted-foreground mt-1">
              {totalCount} total entries
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-48 h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All resources</SelectItem>
              <SelectItem value="health_assessment">Assessments</SelectItem>
              <SelectItem value="appointment">Appointments</SelectItem>
              <SelectItem value="profile">Profiles</SelectItem>
              <SelectItem value="provider_note">Provider Notes</SelectItem>
              <SelectItem value="provider_credential">Provider Changes</SelectItem>
              <SelectItem value="prescription">Prescriptions</SelectItem>
            </SelectContent>
          </Select>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className={cn("h-8 text-xs gap-1.5 min-w-[120px] justify-start", !dateFrom && "text-muted-foreground")}>
                <CalendarIcon size={14} />
                {dateFrom ? format(dateFrom, "MMM d, yyyy") : "From"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar mode="single" selected={dateFrom} onSelect={setDateFrom} initialFocus className="p-3 pointer-events-auto" />
            </PopoverContent>
          </Popover>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className={cn("h-8 text-xs gap-1.5 min-w-[120px] justify-start", !dateTo && "text-muted-foreground")}>
                <CalendarIcon size={14} />
                {dateTo ? format(dateTo, "MMM d, yyyy") : "To"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar mode="single" selected={dateTo} onSelect={setDateTo} initialFocus className="p-3 pointer-events-auto" />
            </PopoverContent>
          </Popover>
          {(dateFrom || dateTo) && (
            <Button variant="ghost" size="sm" className="h-8 text-xs px-2" onClick={() => { setDateFrom(undefined); setDateTo(undefined); }}>
              <X size={14} />
            </Button>
          )}
          <Button variant="outline" size="sm" className="gap-1.5 h-8 text-xs" onClick={exportCsv}>
            <Download size={14} /> Export
          </Button>
          <Button variant="outline" size="icon" onClick={fetchLogs}>
            <RefreshCw size={14} />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : logs.length === 0 ? (
          <p className="text-center text-muted-foreground py-12">No audit logs found</p>
        ) : (
          <div className="space-y-2">
            {logs.map((log) => (
              <div
                key={log.id}
                className="flex items-start gap-3 p-3 rounded-lg border border-border bg-card hover:bg-secondary/30 transition-colors"
              >
                <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center shrink-0 mt-0.5">
                  <ScrollText size={14} className="text-muted-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant={actionColor(log.action)} className="text-xs">
                      {log.action.replace(/_/g, " ")}
                    </Badge>
                    {log.actor_role && ROLE_BADGE[log.actor_role] && (
                      <Badge
                        variant="outline"
                        className={cn("text-[10px] uppercase tracking-wide font-semibold", ROLE_BADGE[log.actor_role].className)}
                        title={`Action performed by a ${ROLE_BADGE[log.actor_role].label}`}
                      >
                        {ROLE_BADGE[log.actor_role].label}
                      </Badge>
                    )}
                    <span className="text-xs text-muted-foreground">{log.resource_type}</span>
                    {log.resource_id && (
                      <span className="text-xs font-mono text-muted-foreground">
                        {log.resource_id.slice(0, 8)}…
                      </span>
                    )}
                  </div>
                  {log.metadata?.provider_name && (
                    <span className="text-xs font-medium text-foreground">{log.metadata.provider_name}</span>
                  )}
                  {log.metadata?.previous_status && (
                    <span className="text-xs text-muted-foreground italic">was: {log.metadata.previous_status}</span>
                  )}
                  {log.metadata?.reason && (
                    <span className="text-xs text-muted-foreground">reason: {log.metadata.reason}</span>
                  )}
                  <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                    <span>User: {log.user_id.slice(0, 8)}…</span>
                    <span>{format(new Date(log.created_at), "MMM d, h:mm a")}</span>
                    {log.ip_address && <span>IP: {log.ip_address}</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
            <p className="text-xs text-muted-foreground">
              Page {page + 1} of {totalPages}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0 || loading}
              >
                <ChevronLeft size={14} className="mr-1" /> Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1 || loading}
              >
                Next <ChevronRight size={14} className="ml-1" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ── Security Tab ─────────────────────────────────────────────────
const SecurityTab = () => {
  const [resetOpen, setResetOpen] = useState(false);
  const [resetConfirm, setResetConfirm] = useState("");
  const [resetting, setResetting] = useState(false);
  const [demoOpen, setDemoOpen] = useState(false);
  const [demoResetting, setDemoResetting] = useState(false);
  const [demoPreview, setDemoPreview] = useState<{
    accounts: number;
    counts: Record<string, number>;
  } | null>(null);
  const [demoPreviewLoading, setDemoPreviewLoading] = useState(false);
  const [demoPreviewError, setDemoPreviewError] = useState<string | null>(null);

  const openDemoResetDialog = async () => {
    setDemoOpen(true);
    setDemoPreview(null);
    setDemoPreviewError(null);
    setDemoPreviewLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("reset-demo-data", {
        body: { dry_run: true },
      });
      if (error) throw error;
      setDemoPreview({
        accounts: data?.accounts ?? 0,
        counts: data?.counts ?? {},
      });
    } catch (err: any) {
      setDemoPreviewError(err.message || "Failed to load preview");
    } finally {
      setDemoPreviewLoading(false);
    }
  };
  const [seeding, setSeeding] = useState(false);
  const { toast } = useToast();

  const handleSeedDemoData = async () => {
    setSeeding(true);
    try {
      const { data, error } = await supabase.functions.invoke("seed-demo-data", { body: {} });
      if (error) throw error;
      const s = data?.seeded || {};
      toast({
        title: "Demo data seeded",
        description: `Created ${s.appointment_id ? "1 appointment" : "0 appointments"}, ${s.prescription_id ? "1 prescription" : "0 prescriptions"}, and 1 notification for the demo patient/provider.`,
      });
    } catch (err: any) {
      toast({ title: "Seed failed", description: err.message, variant: "destructive" });
    } finally {
      setSeeding(false);
    }
  };

  const handleResetAllData = async () => {
    if (resetConfirm !== "RESET") return;
    setResetting(true);
    try {
      const { data, error } = await supabase.functions.invoke("reset-all-data", {
        body: { confirm: "RESET" },
      });
      if (error) throw error;
      toast({ title: "All data wiped", description: "All users and data have been deleted. You will be logged out." });
      // Sign out since the current user was also deleted
      setTimeout(() => {
        supabase.auth.signOut();
      }, 1500);
    } catch (err: any) {
      toast({ title: "Reset failed", description: err.message, variant: "destructive" });
    } finally {
      setResetting(false);
      setResetOpen(false);
      setResetConfirm("");
    }
  };

  const handleResetDemoData = async () => {
    setDemoResetting(true);
    try {
      const { data, error } = await supabase.functions.invoke("reset-demo-data", { body: {} });
      if (error) throw error;
      const d = data?.deleted || {};
      toast({
        title: "Demo data reset",
        description: `Wiped ${d.appointments ?? 0} appointment(s), ${d.prescriptions ?? 0} prescription(s), ${d.notifications ?? 0} notification(s), ${d.health_assessments ?? 0} assessment(s), ${d.messages ?? 0} message(s), and ${d.patient_progress_logs ?? 0} progress log(s) across ${data?.accounts ?? 0} demo account(s).`,
      });
    } catch (err: any) {
      toast({ title: "Demo reset failed", description: err.message, variant: "destructive" });
    } finally {
      setDemoResetting(false);
      setDemoOpen(false);
    }
  };

  const [stats, setStats] = useState({
    totalUsers: 0,
    totalProviders: 0,
    totalAdmins: 0,
    recentActions: 0,
    uniqueActors24h: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      const [profilesRes, rolesRes, logsRes] = await Promise.all([
        supabase.from("profiles").select("user_id", { count: "exact", head: true }),
        supabase.from("user_roles").select("role"),
        supabase
          .from("audit_logs")
          .select("user_id, created_at")
          .gte("created_at", new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()),
      ]);

      const roles = rolesRes.data || [];
      const logs24h = logsRes.data || [];

      setStats({
        totalUsers: profilesRes.count || 0,
        totalProviders: roles.filter((r: any) => r.role === "provider").length,
        totalAdmins: roles.filter((r: any) => r.role === "admin").length,
        recentActions: logs24h.length,
        uniqueActors24h: new Set(logs24h.map((l: any) => l.user_id)).size,
      });
      setLoading(false);
    };
    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const cards = [
    { label: "Total Users", value: stats.totalUsers, icon: Users, color: "text-foreground" },
    { label: "Providers", value: stats.totalProviders, icon: UserCheck, color: "text-champagne" },
    { label: "Admins", value: stats.totalAdmins, icon: Shield, color: "text-primary" },
    { label: "Actions (24h)", value: stats.recentActions, icon: Activity, color: "text-foreground" },
    { label: "Active Users (24h)", value: stats.uniqueActors24h, icon: Users, color: "text-champagne" },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {cards.map((c) => (
          <Card key={c.label} className="border-border">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-muted-foreground">{c.label}</span>
                <c.icon size={16} className={c.color} />
              </div>
              <p className="font-display text-2xl text-foreground">{c.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="border-border">
        <CardHeader>
          <CardTitle className="font-display text-xl flex items-center gap-2">
            <AlertTriangle size={18} className="text-champagne" /> Security Checklist
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-3">
            {[
              { label: "RLS enabled on all tables", status: true },
              { label: "Leaked password (HIBP) protection", status: true },
              { label: "Audit logging via secure RPC", status: true },
              { label: "Provider profiles PII hidden from patients", status: true },
              { label: "Role assignment via SECURITY DEFINER function", status: true },
              { label: "Realtime disabled for sensitive tables", status: true },
            ].map((item) => (
              <li key={item.label} className="flex items-center gap-2 text-sm">
                <span
                  className={`w-2 h-2 rounded-full ${
                    item.status ? "bg-green-500" : "bg-destructive"
                  }`}
                />
                <span className={item.status ? "text-foreground" : "text-destructive"}>
                  {item.label}
                </span>
                <Badge variant="secondary" className="ml-auto text-xs">
                  {item.status ? "Passed" : "Action Needed"}
                </Badge>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      {/* Danger Zone */}
      <Card className="border-destructive/50">
        <CardHeader>
          <CardTitle className="font-display text-xl flex items-center gap-2 text-destructive">
            <AlertTriangle size={18} /> Danger Zone
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-3">
            <div>
              <p className="text-sm font-medium text-foreground">Demo data</p>
              <p className="text-sm text-muted-foreground">
                Seed a sample appointment, prescription, and notification for the demo patient/provider — or wipe everything for the seeded demo accounts (patient@demo.com, provider@demo.com, etc.). The accounts themselves are always preserved.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={handleSeedDemoData} disabled={seeding}>
                <Sparkles size={14} className="mr-1" />
                {seeding ? "Seeding…" : "Seed Demo Data"}
              </Button>
              <Button variant="outline" onClick={openDemoResetDialog}>
                <RefreshCw size={14} className="mr-1" /> Reset Demo Data
              </Button>
            </div>

            <div className="flex items-center gap-3 rounded-md border border-border bg-muted/40 px-3 py-2.5">
              <KeyRound size={16} className="text-muted-foreground shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium text-foreground">Demo password</p>
                <code className="text-xs font-mono text-muted-foreground break-all">
                  ElaraDemo!2026#Seed
                </code>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="shrink-0 gap-1.5"
                onClick={() => {
                  navigator.clipboard.writeText("ElaraDemo!2026#Seed");
                  toast({ title: "Demo password copied" });
                }}
              >
                <Copy size={13} /> Copy
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Set automatically each time Seed Demo Data runs. Applies to patient@demo.com and provider@demo.com.
            </p>
          </div>

          <div className="border-t border-border pt-6 space-y-3">
            <div>
              <p className="text-sm font-medium text-destructive">Reset everything</p>
              <p className="text-sm text-muted-foreground">
                Permanently delete <strong>all users and data</strong> from the system. This action is irreversible.
              </p>
            </div>
            <Button variant="destructive" onClick={() => setResetOpen(true)}>
              <Trash2 size={14} className="mr-1" /> Reset All Data
            </Button>
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={resetOpen} onOpenChange={(o) => { setResetOpen(o); if (!o) setResetConfirm(""); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <strong>all users, orders, assessments, messages, and every other record</strong> in the system. Your own admin account will also be deleted and you will be logged out.
              <br /><br />
              Type <strong>RESET</strong> below to confirm.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            placeholder="Type RESET to confirm"
            value={resetConfirm}
            onChange={(e) => setResetConfirm(e.target.value)}
            className="mt-2"
          />
          <AlertDialogFooter>
            <AlertDialogCancel disabled={resetting}>Cancel</AlertDialogCancel>
            <Button
              variant="destructive"
              disabled={resetConfirm !== "RESET" || resetting}
              onClick={handleResetAllData}
            >
              {resetting ? "Deleting everything…" : "Delete All Data"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={demoOpen} onOpenChange={setDemoOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset demo data?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete records linked to the seeded demo accounts (patient@demo.com, provider@demo.com, etc.). The accounts and their profiles are kept so you can re-test from a clean slate.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="rounded-md border bg-muted/40 p-3 text-sm">
            {demoPreviewLoading && (
              <p className="text-muted-foreground">Calculating what will be deleted…</p>
            )}
            {demoPreviewError && (
              <p className="text-destructive">Couldn't load preview: {demoPreviewError}</p>
            )}
            {demoPreview && !demoPreviewLoading && !demoPreviewError && (
              <>
                <p className="mb-2 font-medium">
                  About to delete across {demoPreview.accounts} demo account
                  {demoPreview.accounts === 1 ? "" : "s"}:
                </p>
                <ul className="space-y-1">
                  {[
                    ["Appointments", demoPreview.counts.appointments],
                    ["Prescriptions", demoPreview.counts.prescriptions],
                    ["Notifications", demoPreview.counts.notifications],
                    ["Health assessments", demoPreview.counts.health_assessments],
                    ["Messages", demoPreview.counts.messages],
                    ["Progress logs", demoPreview.counts.patient_progress_logs],
                  ].map(([label, count]) => (
                    <li key={label as string} className="flex justify-between">
                      <span className="text-muted-foreground">{label}</span>
                      <span className="font-mono font-semibold">{(count as number) ?? 0}</span>
                    </li>
                  ))}
                </ul>
                {Object.values(demoPreview.counts).every((c) => !c) && (
                  <p className="mt-2 text-muted-foreground italic">
                    Nothing to delete — demo data is already clean.
                  </p>
                )}
              </>
            )}
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={demoResetting}>Cancel</AlertDialogCancel>
            <Button
              variant="destructive"
              disabled={demoResetting || demoPreviewLoading || !!demoPreviewError}
              onClick={handleResetDemoData}
            >
              {demoResetting ? "Resetting…" : "Reset demo data"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminDashboard;